import React, { useMemo, useCallback } from "react";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
} from "reactstrap";
import { useGetStatuses, useDeleteStatus } from "api/inventory-status/service";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useTranslation } from "react-i18next";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";

const InventoryStatus = ({ permission, activeColor = "secondary", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: statusesData, isLoading } = useGetStatuses();
  const deleteStatusMutation = useDeleteStatus();
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = React.useState(null);

  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  // Protected status keys that cannot be deleted
  const protectedStatusKeys = ['available', 'sold', 'deleted', 'evaluating'];

  const isStatusProtected = (status) => {
    const statusKey = status.status_key || status.key;
    return protectedStatusKeys.includes(statusKey?.toLowerCase());
  };

  const handleDeleteClick = useCallback((id) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteId) {
      deleteStatusMutation.mutate(selectedDeleteId);
    }
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const statusRows = useMemo(() => {
    const list = statusesData?.results || [];
    return list.map((status) => ({
      id: status.documentId,
      documentId: status.documentId,
      status,
      status_name: status.status_name || status.name,
      status_key: status.status_key || status.key,
      color: status.color,
    }));
  }, [statusesData]);

  const statusTableColumns = useMemo(
    () => [
      { Header: t("inventoryStatus.name"), accessor: "status_name" },
      { Header: t("inventoryStatus.key"), accessor: "status_key" },
      {
        Header: t("inventoryStatus.color"),
        accessor: "color",
        disableSortBy: true,
        Cell: ({ value }) => (
          <div className="d-flex justify-content-center align-items-center w-100">
            <div style={{ width: 20, height: 20, backgroundColor: value || "#ccc" }} />
          </div>
        ),
      },
      {
        id: "actions",
        Header: "",
        accessor: "documentId",
        disableSortBy: true,
        Cell: ({ row }) => {
          const status = row.original.status;
          return (
            <div className="actions-right" style={{ textAlign: "right" }}>
              {permission?.inventory_parameters_status_edit && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/inventory-status/edit/${status.documentId}`);
                  }}
                >
                  <i className="tim-icons icon-pencil" />
                </Button>
              )}
              {permission?.inventory_parameters_status_delete && !isStatusProtected(status) && (
                <Button
                  color="link"
                  size="sm"
                  className="btn-icon btn-link like table-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(status.documentId);
                  }}
                  disabled={deleteStatusMutation.isPending}
                >
                  {deleteStatusMutation.isPending && selectedDeleteId === status.documentId ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  ) : (
                    <i className="tim-icons icon-simple-remove" />
                  )}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      permission?.inventory_parameters_status_delete,
      permission?.inventory_parameters_status_edit,
      navigate,
      handleDeleteClick,
      deleteStatusMutation.isPending,
      selectedDeleteId,
    ]
  );

  const StatusTable = shellThemeVariant === "2" ? ReactTable2 : ReactTable;

  return (
    <div className="tab-content">
      <style>
          {`
          .sticky-stock-table .rt-table {
            width: 100%;
            min-width: 100%;
            border-collapse: collapse;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
            text-align: center !important;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child {
            text-align: right !important;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
            text-align: center;
            width: 100%;
            box-sizing: border-box;
          }
          .sticky-stock-table .rt-thead .rt-th:not(:last-child).-cursor-pointer .rt-resizable-header-content {
            padding-right: 2em;
          }
          .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
            text-align: right;
            width: 100%;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
            text-align: center;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:last-child {
            text-align: right;
            vertical-align: middle;
          }
          .sticky-stock-table .rt-tbody .rt-td:not(:last-child) > * {
            max-width: 100%;
            min-width: 0;
          }
            ${getStickyStockTableStickyColumnCss("")}
        `}
      </style>
      <Row>
        <Col md="12">
          <Card>
            <CardHeader className="inventory-status-card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-0">{t('routes.inventoryParameters')}</h2>
                {permission?.inventory_parameters_status_add && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/inventory-status/add')}
                    style={{ ...activeColorStyle, color: 'white' }}
                  >
                    {t('inventoryStatus.addStatus')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              <div
                className="inventory-table-wrap"
                style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
              >
                <StatusTable
                    activeColor={activeColor}
                    data={statusRows}
                    columns={statusTableColumns}
                    className="-striped -highlight sticky-stock-table"
                    manualPagination={false}
                    pageSize={20}
                    loading={isLoading}
                    loadingText={t("inventoryStatus.loading")}
                    noDataText={t("inventoryStatus.noStatusesFound")}
                    onRowClick={(row) => {
                      if (row?.documentId) {
                        navigate(`/inventory-status/edit/${row.documentId}`);
                      }
                    }}
                  />
                </div>

            </CardBody>
          </Card>
        </Col>
      </Row>
      <Modal isOpen={deleteModalOpen} toggle={handleCancelDelete} centered={false} style={{ marginTop: '100px' }}>
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t('inventoryStatus.deleteStatus')}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t('inventoryStatus.deleteConfirmation')}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" type="button" onClick={handleCancelDelete}>
            {t('inventoryStatus.cancel')}
          </Button>
          <Button
            style={{
              background: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              backgroundImage: 'linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)',
              borderColor: '#fd5d93',
              color: 'white'
            }} color="danger" type="button" onClick={handleConfirmDelete}>
            {t('inventoryStatus.yesDelete')}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default InventoryStatus; 